/**
 * 360°/equirectangular detection signals.
 *
 * Two independent signals are derived from a file, in a single parse:
 *
 *  1. `metadataType` — AUTHORITATIVE. Read from embedded metadata only (never
 *     pixel dimensions), so a positive result means the producing tool explicitly
 *     tagged the file as a 360° panorama. This is what drives classification.
 *       - JPEG: Google Photo Sphere XMP (GPano namespace) in an APP1 segment,
 *               where GPano:ProjectionType == equirectangular.
 *       - PNG:  the same XMP packet carried in an `iTXt` chunk (XML:com.adobe.xmp).
 *       - MP4/MOV (ISOBMFF): Spherical Video V2 (`sv3d` with an `equi` projection
 *               box) or V1 (the spherical `uuid` box whose GSpherical XML is
 *               equirectangular).
 *       - WebM (Matroska): Video > Projection > ProjectionType == 1.
 *
 *  2. `hasEquirectangularAspectRatio` — a WEAK HINT. True when the pixel
 *     dimensions are ~2:1, the shape of a full equirectangular frame. Wide non-360
 *     content (banners, ultrawide) is also 2:1, so this must NEVER classify on its
 *     own — it exists to let a caller surface a "looks like 360°, confirm?" prompt
 *     for files that carry no metadata. Currently unused; exposed for future use.
 */

import { MediaType } from '../types';

const LATIN1 = new TextDecoder('latin1');

// A full equirectangular frame is exactly 2:1; allow a little slack for odd sizes.
const EQUIRECTANGULAR_ASPECT_RATIO = 2;
const ASPECT_RATIO_TOLERANCE = 0.01;

export interface Media360Signals {
  /**
   * The 360 MediaType when embedded metadata authoritatively identifies the file
   * as equirectangular, otherwise null. Safe to classify on.
   */
  metadataType: MediaType.Image360 | MediaType.Video360 | null;
  /**
   * Weak hint that the pixel dimensions are ~2:1. NOT sufficient to classify on
   * its own — intended only for prompting the user to confirm the type. False
   * when the dimensions can't be determined.
   */
  hasEquirectangularAspectRatio: boolean;
  /** Pixel dimensions when determinable from the header/container, else null. */
  width: number | null;
  height: number | null;
}

/**
 * Probes a file for both 360 signals in a single parse. Best-effort: any parse
 * failure resolves to "no signals" rather than throwing, so callers can use it
 * inline without guarding.
 *
 * IMPORTANT: for images this must run on the ORIGINAL file. compressImageForUpload
 * re-encodes images through a canvas and re-injects only EXIF, stripping the GPano
 * XMP packet `metadataType` relies on.
 */
export async function detect360(file: File): Promise<Media360Signals> {
  try {
    const kind = classify(file);
    const probe =
      kind === 'image' ? await probeImage(file)
      : kind === 'video' ? await probeVideo(file)
      : EMPTY_PROBE;

    const metadataType = !probe.is360 ? null
      : kind === 'image' ? MediaType.Image360
      : MediaType.Video360;

    return {
      metadataType,
      hasEquirectangularAspectRatio: isEquirectangularAspectRatio(probe.width, probe.height),
      width: probe.width,
      height: probe.height,
    };
  } catch (err) {
    // Detection is best-effort — a parse failure must never block an upload.
    console.warn('[detect360] detection failed, treating as non-360:', file.name, err);
    return { metadataType: null, hasEquirectangularAspectRatio: false, width: null, height: null };
  }
}

/**
 * Convenience wrapper returning only the authoritative, metadata-derived 360
 * MediaType (or null). This is what the upload pipeline classifies on; the
 * aspect-ratio hint is intentionally ignored here.
 */
export async function detect360MediaType(
  file: File,
): Promise<MediaType.Image360 | MediaType.Video360 | null> {
  return (await detect360(file)).metadataType;
}

/**
 * The 360 MediaType corresponding to a file's kind — Image360 for images,
 * Video360 for videos, null otherwise. For callers that have decided, from
 * context, to treat an equirectangular-aspect-ratio file as 360 and need the
 * matching type (the aspect-ratio hint never classifies on its own).
 */
export function corresponding360Type(file: File): MediaType.Image360 | MediaType.Video360 | null {
  const kind = classify(file);
  return kind === 'image' ? MediaType.Image360 : kind === 'video' ? MediaType.Video360 : null;
}

function isEquirectangularAspectRatio(width: number | null, height: number | null): boolean {
  if (!width || !height) return false;
  return Math.abs(width / height - EQUIRECTANGULAR_ASPECT_RATIO) < ASPECT_RATIO_TOLERANCE;
}

// What a single-file parse yields: the metadata verdict plus pixel dimensions.
interface MediaProbe {
  is360: boolean;
  width: number | null;
  height: number | null;
}
const EMPTY_PROBE: MediaProbe = { is360: false, width: null, height: null };

type FileKind = 'image' | 'video' | 'other';

function classify(file: File): FileKind {
  const mime = file.type.toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  // MIME can be absent (e.g. files extracted from a ZIP) — fall back to extension.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
  if (['mp4', 'm4v', 'mov', 'webm'].includes(ext)) return 'video';
  return 'other';
}

// ─── Images: GPano XMP + dimensions ────────────────────────────────────────────

// GPano XMP and the SOF/IHDR dimensions both sit within the first metadata bytes.
const IMAGE_HEADER_BYTES = 1024 * 1024;
const XMP_NS_SIGNATURE = 'http://ns.adobe.com/xap/1.0/';
const PNG_XMP_KEYWORD = 'XML:com.adobe.xmp';

async function probeImage(file: File): Promise<MediaProbe> {
  const head = new Uint8Array(await file.slice(0, IMAGE_HEADER_BYTES).arrayBuffer());

  // JPEG — SOI marker FF D8
  if (head[0] === 0xff && head[1] === 0xd8) return jpegProbe(head);
  // PNG — signature 89 50 4E 47 0D 0A 1A 0A
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return pngProbe(head);
  return EMPTY_PROBE;
}

/**
 * Walks the JPEG marker chain, collecting the GPano XMP verdict (from an APP1
 * segment) and the frame dimensions (from the SOF segment).
 */
function jpegProbe(bytes: Uint8Array): MediaProbe {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 2; // skip SOI
  let is360 = false;
  let width: number | null = null;
  let height: number | null = null;

  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) break;
    // Skip any 0xFF fill bytes preceding the marker code.
    while (offset + 1 < bytes.length && bytes[offset + 1] === 0xff) offset++;

    const marker = view.getUint16(offset);
    if (marker === 0xffda || marker === 0xffd9) break; // SOS / EOI — metadata done

    const segLength = view.getUint16(offset + 2); // includes its own 2 length bytes
    if (segLength < 2) break;
    const dataStart = offset + 4;
    const dataEnd = Math.min(dataStart + segLength - 2, bytes.length);

    if (marker === 0xffe1) {
      // APP1 — could be EXIF or XMP; we only care about the XMP packet.
      const segment = bytes.subarray(dataStart, dataEnd);
      if (startsWithAscii(segment, XMP_NS_SIGNATURE) && isEquirectangularXmp(LATIN1.decode(segment))) {
        is360 = true;
      }
    } else if (isSofMarker(marker) && dataStart + 5 <= bytes.length) {
      // SOF payload: [precision:1][height:2][width:2]...
      height = view.getUint16(dataStart + 1);
      width = view.getUint16(dataStart + 3);
    }

    offset += 2 + segLength;
  }
  return { is360, width, height };
}

/** Start-of-Frame markers carry dimensions; exclude DHT/JPG/DAC (C4/C8/CC). */
function isSofMarker(marker: number): boolean {
  return (marker & 0xfff0) === 0xffc0 && marker !== 0xffc4 && marker !== 0xffc8 && marker !== 0xffcc;
}

/**
 * Walks PNG chunks, collecting dimensions (IHDR) and the GPano XMP verdict
 * (iTXt chunk with the XMP keyword).
 */
function pngProbe(bytes: Uint8Array): MediaProbe {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8; // skip the 8-byte PNG signature
  let is360 = false;
  let width: number | null = null;
  let height: number | null = null;

  while (offset + 8 <= bytes.length) {
    const len = view.getUint32(offset);
    const type = LATIN1.decode(bytes.subarray(offset + 4, offset + 8));
    const dataStart = offset + 8;
    const dataEnd = Math.min(dataStart + len, bytes.length);

    if (type === 'IHDR' && dataStart + 8 <= bytes.length) {
      width = view.getUint32(dataStart);
      height = view.getUint32(dataStart + 4);
    } else if (type === 'iTXt') {
      const chunk = bytes.subarray(dataStart, dataEnd);
      // iTXt layout: keyword \0 ... text. XMP keyword is "XML:com.adobe.xmp".
      if (startsWithAscii(chunk, PNG_XMP_KEYWORD) && isEquirectangularXmp(LATIN1.decode(chunk))) {
        is360 = true;
      }
    }

    if (type === 'IDAT' || type === 'IEND') break; // image data — metadata comes before
    offset += 8 + len + 4; // length + type + data + CRC
  }
  return { is360, width, height };
}

/**
 * True when an XMP packet declares an equirectangular GPano projection.
 * `equirectangular` only ever appears as the GPano:ProjectionType value, so its
 * presence in a GPano/ProjectionType context is an authoritative signal.
 */
function isEquirectangularXmp(xmp: string): boolean {
  return /equirectangular/i.test(xmp) && /GPano|ProjectionType/i.test(xmp);
}

// ─── Videos: ISOBMFF (MP4/MOV) & Matroska (WebM) ───────────────────────────────

// Spherical Video V1 metadata UUID: ffcc8263-f855-4a93-8814-587a02521fdd
const SPHERICAL_V1_UUID = [
  0xff, 0xcc, 0x82, 0x63, 0xf8, 0x55, 0x4a, 0x93,
  0x88, 0x14, 0x58, 0x7a, 0x02, 0x52, 0x1f, 0xdd,
];
// Safety ceiling on the moov box read; spherical metadata sits near its head.
const MAX_MOOV_BYTES = 32 * 1024 * 1024;
// Matroska track metadata precedes the media clusters, so a bounded read suffices.
const WEBM_HEADER_BYTES = 4 * 1024 * 1024;

async function probeVideo(file: File): Promise<MediaProbe> {
  const sig = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  // Matroska/WebM begins with the EBML magic 1A 45 DF A3.
  if (sig[0] === 0x1a && sig[1] === 0x45 && sig[2] === 0xdf && sig[3] === 0xa3) {
    return webmProbe(file);
  }
  // Otherwise assume ISOBMFF (MP4/MOV/M4V): locate and scan the moov box.
  const moov = await readMoovBox(file);
  return moov ? moovProbe(moov) : EMPTY_PROBE;
}

/**
 * Locates the top-level `moov` box and returns its bytes. Walks box headers only,
 * skipping past `mdat` (and any other box) via its size field without reading the
 * payload, so it stays cheap even when moov lives at the end of a large file.
 */
async function readMoovBox(file: File): Promise<Uint8Array | null> {
  const fileSize = file.size;
  let pos = 0;

  while (pos + 8 <= fileSize) {
    const header = new Uint8Array(await file.slice(pos, pos + 16).arrayBuffer());
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength);

    let boxSize = view.getUint32(0);
    const type = LATIN1.decode(header.subarray(4, 8));
    let headerLen = 8;

    if (boxSize === 1) {
      // 64-bit size follows the type.
      boxSize = view.getUint32(8) * 2 ** 32 + view.getUint32(12);
      headerLen = 16;
    } else if (boxSize === 0) {
      boxSize = fileSize - pos; // extends to EOF
    }

    if (boxSize < headerLen) break; // malformed

    if (type === 'moov') {
      const end = Math.min(pos + Math.min(boxSize, MAX_MOOV_BYTES), fileSize);
      return new Uint8Array(await file.slice(pos, end).arrayBuffer());
    }

    pos += boxSize;
  }
  return null;
}

function moovProbe(moov: Uint8Array): MediaProbe {
  return { is360: moovIsEquirectangular(moov), ...trackDimensions(moov) };
}

function moovIsEquirectangular(moov: Uint8Array): boolean {
  // Spherical Video V2: an `sv3d` box with an `equi` (equirectangular) projection
  // box. `equi` distinguishes equirectangular from cubemap (`cbmp`) / mesh.
  if (containsBoxType(moov, 'sv3d') && containsBoxType(moov, 'equi')) return true;

  // Spherical Video V1: the spherical `uuid` box, followed by an XML blob whose
  // GSpherical:ProjectionType is equirectangular.
  const uuidIdx = indexOfBytes(moov, SPHERICAL_V1_UUID);
  if (uuidIdx >= 0) {
    const xml = LATIN1.decode(moov.subarray(uuidIdx, Math.min(uuidIdx + 4096, moov.length)));
    if (/equirectangular/i.test(xml)) return true;
  }
  return false;
}

/**
 * Reads the video track's pixel dimensions from a `tkhd` box. tkhd stores width
 * and height as 16.16 fixed-point; the audio track's are zero, so we take the
 * first track with non-zero dimensions.
 */
function trackDimensions(moov: Uint8Array): { width: number | null; height: number | null } {
  const view = new DataView(moov.buffer, moov.byteOffset, moov.byteLength);
  // 'tkhd' = 74 6B 68 64
  for (let i = 4; i + 4 <= moov.length; i++) {
    if (moov[i] !== 0x74 || moov[i + 1] !== 0x6b || moov[i + 2] !== 0x68 || moov[i + 3] !== 0x64) continue;

    const boxSize = view.getUint32(i - 4);
    if (boxSize < 8 || boxSize > moov.length) continue; // not a real box header

    const payload = i + 4;
    // Version 1 widens the time fields by 12 bytes, pushing width/height back.
    const base = moov[payload] === 1 ? 88 : 76;
    if (payload + base + 8 > moov.length) continue;

    const width = view.getUint32(payload + base) >>> 16; // 16.16 fixed-point → integer part
    const height = view.getUint32(payload + base + 4) >>> 16;
    if (width > 0 && height > 0) return { width, height };
  }
  return { width: null, height: null };
}

/**
 * Scans for an ISOBMFF box of the given 4-char type. A box type is preceded by
 * its 4-byte big-endian size, so we validate that to avoid matching the same
 * bytes occurring inside other data.
 */
function containsBoxType(buf: Uint8Array, type: string): boolean {
  const [a, b, c, d] = [0, 1, 2, 3].map((i) => type.charCodeAt(i));
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  for (let i = 4; i + 4 <= buf.length; i++) {
    if (buf[i] === a && buf[i + 1] === b && buf[i + 2] === c && buf[i + 3] === d) {
      const boxSize = view.getUint32(i - 4);
      if (boxSize === 1 || (boxSize >= 8 && boxSize <= buf.length)) return true;
    }
  }
  return false;
}

function indexOfBytes(haystack: Uint8Array, needle: number[]): number {
  outer: for (let i = 0; i + needle.length <= haystack.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

// EBML element IDs, in canonical form (i.e. including the length-descriptor bit).
const EBML_SEGMENT = 0x18538067;
const EBML_TRACKS = 0x1654ae6b;
const EBML_TRACK_ENTRY = 0xae;
const EBML_VIDEO = 0xe0;
const EBML_PROJECTION = 0x7670;
const EBML_PROJECTION_TYPE = 0x7671;
const EBML_PIXEL_WIDTH = 0xb0;
const EBML_PIXEL_HEIGHT = 0xba;
const EBML_CLUSTER = 0x1f43b675;
// Master elements we descend through to reach Video > Projection > ProjectionType.
const EBML_CONTAINERS = new Set([EBML_SEGMENT, EBML_TRACKS, EBML_TRACK_ENTRY, EBML_VIDEO, EBML_PROJECTION]);

/**
 * Parses the Matroska/WebM element tree for the equirectangular Projection
 * (Segment > Tracks > TrackEntry > Video > Projection > ProjectionType == 1) and
 * the Video element's PixelWidth/PixelHeight, in one walk. Track metadata always
 * precedes the media clusters, so the walk stops at the first Cluster and only
 * inspects the header region — never the cluster payload, where a naive byte
 * search would hit countless false matches.
 */
async function webmProbe(file: File): Promise<MediaProbe> {
  const bytes = new Uint8Array(await file.slice(0, Math.min(WEBM_HEADER_BYTES, file.size)).arrayBuffer());
  const probe: MediaProbe = { is360: false, width: null, height: null };
  walkEbml(bytes, 0, bytes.length, probe);
  return probe;
}

function walkEbml(bytes: Uint8Array, start: number, end: number, out: MediaProbe): void {
  let pos = start;
  while (pos < end) {
    const id = readEbmlVint(bytes, pos, true);
    if (!id) break;
    const size = readEbmlVint(bytes, pos + id.len, false);
    if (!size) break;

    if (id.value === EBML_CLUSTER) break; // media data begins — no track metadata beyond here

    const dataStart = pos + id.len + size.len;
    const dataEnd = size.unknown ? end : Math.min(dataStart + size.value, end);

    // ProjectionType / PixelWidth / PixelHeight are plain size-byte unsigned ints.
    if (id.value === EBML_PROJECTION_TYPE) {
      if (readUintBE(bytes, dataStart, size.value) === 1) out.is360 = true; // 1 == equirectangular
    } else if (id.value === EBML_PIXEL_WIDTH) {
      out.width = readUintBE(bytes, dataStart, size.value) ?? out.width;
    } else if (id.value === EBML_PIXEL_HEIGHT) {
      out.height = readUintBE(bytes, dataStart, size.value) ?? out.height;
    } else if (EBML_CONTAINERS.has(id.value)) {
      walkEbml(bytes, dataStart, dataEnd, out);
    }

    pos = dataEnd;
  }
}

/**
 * Reads an EBML variable-length integer at `pos`. With keepMarker=true the
 * length-descriptor bit is preserved, yielding canonical element IDs; with false
 * it is stripped, yielding element sizes. A size whose value bits are all ones
 * means "unknown size", reported via `unknown`.
 */
function readEbmlVint(
  bytes: Uint8Array,
  pos: number,
  keepMarker: boolean,
): { value: number; len: number; unknown: boolean } | null {
  const first = bytes[pos];
  if (!first) return null; // past EOF, or an invalid 0x00 lead byte

  let len = 1;
  let mask = 0x80;
  while (len <= 8 && !(first & mask)) {
    mask >>= 1;
    len++;
  }
  if (len > 8 || pos + len > bytes.length) return null;

  let value = keepMarker ? first : first & (mask - 1);
  let unknown = !keepMarker && (first & (mask - 1)) === mask - 1;
  for (let k = 1; k < len; k++) {
    value = value * 256 + bytes[pos + k];
    if (bytes[pos + k] !== 0xff) unknown = false;
  }
  return { value, len, unknown };
}

/** Reads a big-endian unsigned integer of `len` bytes (an EBML element's value). */
function readUintBE(bytes: Uint8Array, pos: number, len: number): number | null {
  if (len < 1 || len > 8 || pos + len > bytes.length) return null;
  let value = 0;
  for (let k = 0; k < len; k++) value = value * 256 + bytes[pos + k];
  return value;
}

// ─── Shared helpers ────────────────────────────────────────────────────────────

function startsWithAscii(bytes: Uint8Array, prefix: string): boolean {
  if (bytes.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (bytes[i] !== prefix.charCodeAt(i)) return false;
  }
  return true;
}
